import { when } from "mobx";
import { logger } from "@/modules/logger";
import { AudioRecordingRepository } from "@/domains/dexie-db/repositories/audio-recording/audio-recording";
import { Uuid } from "@/domains/global/identifiers";
import {
  AudioRecordingTransformProgressTracker,
  AudioRecordingTransformingState,
  TransformMode,
  TransformPhase,
} from "@/store/audio/transforming/audio-transforming.types";
import { uuidModule } from "@/modules/uuid";
import type { AppStore } from "@/store/AppStore";
import { UpsertNoteSuggestionOperation } from "@/store/sync/operations/notes/UpsertNoteSuggestionOperation";
import type { UpsertNoteSuggestionAudioRecordingMergeIntentPayload } from "@/store/sync/operations/types";
import { buildNoteSuggestionMergeIntentPayload } from "@/store/audio/audio-recording-merge-intent";
import { noteModule } from "@mem-labs/common-editor";
import { processAudioRecording } from "@/store/audio/transforming/audio-recording-transformer.utils";
import { resolveNoteSuggestionBaseContent } from "@/store/note/helpers/note-suggestion-content";
import {
  AudioRecordingTranscriptStatus,
  IAudioTranscriptionStore,
} from "@/store/audio/transcription";

type TransformJob = {
  audioRecordingId: Uuid;
};

type Dependencies = {
  store: AppStore;
  audioRecordings: AudioRecordingRepository;
  transcriptionStore: IAudioTranscriptionStore;
  progressTracker: AudioRecordingTransformProgressTracker;
};

export class AudioRecordingTransformer {
  private readonly deps: Dependencies;
  private readonly queuedJobs: TransformJob[] = [];
  private readonly queuedJobKeys = new Set<Uuid>();
  private isRunning = false;

  constructor(deps: Dependencies) {
    this.deps = deps;
  }

  enqueue(job: TransformJob): void {
    const key = this.buildJobKey(job.audioRecordingId);
    if (this.queuedJobKeys.has(key)) return;

    this.queuedJobKeys.add(key);
    this.queuedJobs.push(job);

    // Set transform state immediately so UI shows "Processing" instead of "Uploading"
    this.deps.progressTracker.setTransformState({
      audioRecordingId: job.audioRecordingId,
      state: {
        isTransforming: true,
        errorMessage: null,
        apiCallCompletedAt: null,
        phase: TransformPhase.Processing,
        mode: TransformMode.StandardFlow,
      },
    });

    void this.runQueue();
  }

  get debugQueuedJobs(): TransformJob[] {
    return [...this.queuedJobs];
  }

  private async runQueue(): Promise<void> {
    if (this.isRunning) {
      return;
    }
    this.isRunning = true;
    try {
      while (this.queuedJobs.length) {
        const job = this.queuedJobs[0];
        const key = this.buildJobKey(job.audioRecordingId);
        try {
          await this.processTransformJob(job);
        } catch (error) {
          logger.error({
            message: "[AudioRecordingTransformer] transform failure",
            info: { error: error instanceof Error ? error.message : String(error) },
          });
        } finally {
          this.queuedJobs.shift();
          this.queuedJobKeys.delete(key);
        }
      }
    } finally {
      this.isRunning = false;
    }
  }

  private async processTransformJob({ audioRecordingId }: TransformJob): Promise<void> {
    const { audioRecordings, progressTracker, store } = this.deps;

    const updateState = (state: Omit<AudioRecordingTransformingState, "mode">) =>
      progressTracker.setTransformState({
        audioRecordingId,
        state: { ...state, mode: TransformMode.StandardFlow },
      });

    const recording = await audioRecordings.getById({ id: audioRecordingId });
    if (!recording) {
      logger.warn({
        message: "[AudioRecordingTransformer] Missing audio recording",
        info: { audioRecordingId },
      });
      updateState({
        isTransforming: false,
        errorMessage: "Recording not found",
        apiCallCompletedAt: null,
        phase: TransformPhase.Idle,
      });
      return;
    }

    // Ensure the recording is loaded so optimistic error updates apply locally.
    await store.audioRecordings.getAsync(audioRecordingId);
    if (recording.data.trashed_at) {
      logger.info({
        message: "[AudioRecordingTransformer] Recording was trashed before transform",
        info: { audioRecordingId },
      });
      progressTracker.clearTransformState({ audioRecordingId });
      this.deps.transcriptionStore.clearTranscriptionStatus({ audioRecordingId });
      return;
    }

    const noteId = recording.data.associated_note_id;
    if (!noteId) {
      logger.warn({
        message: "[AudioRecordingTransformer] Missing associated note id",
        info: { audioRecordingId },
      });
      await audioRecordings.markAsUnexpectedError({ id: audioRecordingId });
      updateState({
        isTransforming: false,
        errorMessage: "Recording is not linked to a note",
        apiCallCompletedAt: null,
        phase: TransformPhase.Idle,
      });
      return;
    }

    // Transcript-source resolution is the pipeline's job. By contract, when
    // this worker runs, the transcript is Ready (local) or has already been
    // backfilled by a retranscribe stage that updated the local transcript.
    const transcriptState = await this.deps.transcriptionStore.getTranscriptState({
      audioRecordingId,
    });

    if (transcriptState.status !== AudioRecordingTranscriptStatus.Ready) {
      logger.error({
        message: "[AudioRecordingTransformer] Transcript not Ready at transform start",
        info: { audioRecordingId, status: transcriptState.status },
      });
      updateState({
        isTransforming: false,
        errorMessage: "Transcript not available",
        apiCallCompletedAt: null,
        phase: TransformPhase.Idle,
      });
      return;
    }

    const transcriptContent = transcriptState.content;
    const transcriptArray = transcriptState.transcriptArray;

    const note = await store.notes.getAsync(noteId);
    if (!note || !note.isAvailable) {
      logger.warn({
        message: "[AudioRecordingTransformer] Note not found",
        info: { audioRecordingId, noteId, noteAvailable: note?.isAvailable ?? false },
      });
      await audioRecordings.markAsUnexpectedError({ id: audioRecordingId });
      updateState({
        isTransforming: false,
        errorMessage: "Note not found",
        apiCallCompletedAt: null,
        phase: TransformPhase.Idle,
      });
      return;
    }

    await audioRecordings.markAsProcessingInProgress({ id: audioRecordingId });
    note.setIsProcessingSuggestion(true);
    updateState({
      isTransforming: true,
      errorMessage: null,
      apiCallCompletedAt: null,
      phase: TransformPhase.Processing,
    });

    try {
      const {
        encodedContent: prevEncodedContent,
        markdownContent: prevMarkdownContent,
        primaryLabel: prevPrimaryLabel,
        secondaryLabel: prevSecondaryLabel,
      } = await resolveNoteSuggestionBaseContent(note);

      const associatedCapturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
        associatedAudioRecordingId: audioRecordingId,
      });
      const associatedSessionId = uuidModule.resolveCapturedAudioSessionSyncModelUuid({
        capturedAudioId: associatedCapturedAudioId,
      });

      const { suggestedMarkdownContent } = await processAudioRecording({
        audioRecordingId,
        transcriptContent,
        associatedNoteMarkdownContent: prevMarkdownContent,
        associatedSessionId,
        transcriptArray,
      });

      const latestRecording = await audioRecordings.getById({ id: audioRecordingId });
      if (!latestRecording || latestRecording.data.trashed_at) {
        logger.info({
          message: "[AudioRecordingTransformer] Recording was trashed during transform",
          info: { audioRecordingId },
        });
        progressTracker.clearTransformState({ audioRecordingId });
        this.deps.transcriptionStore.clearTranscriptionStatus({ audioRecordingId });
        return;
      }

      const audioRecordingMergeIntent = buildNoteSuggestionMergeIntentPayload({
        audioRecordingId,
        modelData: latestRecording.data,
      });

      await this.createSuggestion({
        noteId,
        prevEncodedContent,
        prevMarkdownContent,
        suggestedMarkdownContent,
        prevPrimaryLabel,
        prevSecondaryLabel,
        transcriptContent,
        associatedSessionId,
        audioRecordingId,
        audioRecordingMergeIntent,
      });

      await audioRecordings.markAsProcessingCompleted({ id: audioRecordingId });

      // Wait for the suggestion to appear on the note.
      await when(() => note.isSuggestionActive, { timeout: 5000 }).catch(() => {});
      // Add a slight delay so the suggestion has time to be applied to the editor,
      // allowing the pulse animation to fade into the blue suggestion diff background.
      await new Promise(resolve => setTimeout(resolve, 500));

      updateState({
        isTransforming: false,
        errorMessage: null,
        apiCallCompletedAt: Date.now(),
        phase: TransformPhase.Idle,
      });
      this.deps.transcriptionStore.clearTranscriptionStatus({ audioRecordingId });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      await audioRecordings.markAsProcessingErrored({ id: audioRecordingId });

      updateState({
        isTransforming: false,
        errorMessage: message,
        apiCallCompletedAt: null,
        phase: TransformPhase.Idle,
      });

      logger.error({
        message: "[AudioRecordingTransformer] Transform request failed",
        info: { audioRecordingId, error: message },
      });

      this.deps.transcriptionStore.clearTranscriptionStatus({ audioRecordingId });
    } finally {
      // Ensure note actions re-enable even if the transform exits early (e.g. recording trashed mid-transform).
      note.setIsProcessingSuggestion(false);
    }
  }

  private async createSuggestion({
    noteId,
    prevEncodedContent,
    prevMarkdownContent,
    suggestedMarkdownContent,
    prevPrimaryLabel,
    prevSecondaryLabel,
    transcriptContent,
    associatedSessionId,
    audioRecordingId,
    audioRecordingMergeIntent,
  }: {
    noteId: Uuid;
    prevEncodedContent: string;
    prevMarkdownContent: string;
    suggestedMarkdownContent: string;
    prevPrimaryLabel: string;
    prevSecondaryLabel: string;
    transcriptContent: string;
    associatedSessionId: Uuid;
    audioRecordingId: Uuid;
    audioRecordingMergeIntent?: UpsertNoteSuggestionAudioRecordingMergeIntentPayload;
  }): Promise<void> {
    const { store } = this.deps;

    const {
      suggestedEncodedContent,
      suggestedEncodedContentDiff,
      suggestedPrimaryLabel,
      suggestedSecondaryLabel,
    } = noteModule.buildSuggestionData({
      previousEncodedContent: prevEncodedContent,
      suggestedMarkdownContent,
    });
    const capturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
      associatedAudioRecordingId: audioRecordingId,
    });
    const suggestionId = uuidModule.generate();

    await new UpsertNoteSuggestionOperation({
      store,
      payload: {
        suggestion_id: suggestionId,
        note_id: noteId,
        kind: "VOICE_SESSION_V1",
        prev_encoded_content: prevEncodedContent,
        prev_primary_label: prevPrimaryLabel,
        prev_secondary_label: prevSecondaryLabel,
        prev_markdown_content: prevMarkdownContent,
        suggested_encoded_content: suggestedEncodedContent,
        suggested_encoded_content_diff: suggestedEncodedContentDiff,
        suggested_primary_label: suggestedPrimaryLabel,
        suggested_secondary_label: suggestedSecondaryLabel,
        suggested_markdown_content: suggestedMarkdownContent,
        metadata: {
          session_id: associatedSessionId,
          captured_audio_id: capturedAudioId,
          transcript_text: transcriptContent,
          audio_recording_id: audioRecordingId,
        },
        ...(audioRecordingMergeIntent
          ? { audio_recording_merge_intent: audioRecordingMergeIntent }
          : {}),
      },
    }).execute();
  }

  private buildJobKey(audioRecordingId: Uuid): string {
    return audioRecordingId;
  }

  resetState() {
    this.queuedJobs.splice(0);
    this.queuedJobKeys.clear();
    this.isRunning = false;
  }
}
